import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarCheck, User } from "lucide-react";
import { useRecordContext } from "ra-core";
import {
  DateField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
} from "../common";
import { HRStatusBadge } from "../HRStatusBadge";
import { LeaveApprovalActions } from "./LeaveApprovalActions";

const LeaveRequestTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  const range = record.from_date
    ? `${new Date(record.from_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}${record.to_date && record.to_date !== record.from_date ? ` – ${new Date(record.to_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}`
    : "";
  return <span>Leave Request{range ? ` (${range})` : ""}</span>;
};

const StatusText = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <HRStatusBadge status={record.status || ''} />;
};

export const LeaveRequestShow = () => (
  <Show title={<LeaveRequestTitle />} actions={<LeaveApprovalActions />}>
    <Card className="w-full shadow-sm max-w-4xl mx-auto">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg font-semibold tracking-tight">Leave Request Details</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
      <SimpleShowLayout>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <User className="w-3.5 h-3.5" /> Employee Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div>
                <ReferenceField source="employee_id" reference="employees">
                  <TextField source="name" className="font-semibold" />
                </ReferenceField>
              </div>
              <div>
                <ReferenceField source="leave_type_id" reference="hr_leave_types">
                  <TextField source="name" className="font-medium" />
                </ReferenceField>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Duration & Status
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <DateField source="from_date" className="text-sm" />
                <span className="text-xs text-muted-foreground">to</span>
                <DateField source="to_date" className="text-sm" />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">{`Days: `}<TextField source="days" /></span>
                <StatusText />
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Reason & Approval
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div>
                <span className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Reason</span>
                <TextField source="reason" className="text-sm italic text-muted-foreground" />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Approver</span>
                  <ReferenceField source="approver_id" reference="employees" emptyText="Not yet approved">
                    <TextField source="name" className="text-sm" />
                  </ReferenceField>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Approved At</span>
                  <DateField source="approved_at" showTime className="text-sm" emptyText="—" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </SimpleShowLayout>
      </CardContent>
    </Card>
  </Show>
);

